/**
 * tournament-api.ts - API client for tournament operations
 *
 * Handles all HTTP requests related to tournament management,
 * pairings, standings, and scheduling.
 */

import type {
  Tournament,
  CreateTournamentRequest,
  UpdateTournamentRequest,
  StandingsResponse,
  ShareLinkResponse,
  Round,
  UpdateRoundScheduleRequest,
  GeneratePairingsRequest,
  ApiError
} from '../../types/api';
import { API_BASE_URL } from '../utils/constants';

/**
 * Fetch all tournaments
 * @returns Array of tournament objects
 * @throws Error if request fails
 */
export async function fetchTournaments(): Promise<Tournament[]> {
  const response = await fetch(API_BASE_URL, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch tournaments');
  }

  return await response.json();
}

/**
 * Fetch a single tournament by ID
 * @param id - Tournament ID
 * @returns Tournament object
 * @throws Error if request fails
 */
export async function fetchTournamentById(id: string): Promise<Tournament> {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch tournament');
  }

  return await response.json();
}

/**
 * Create a new tournament
 * @param tournamentData - Tournament data
 * @returns Created tournament object
 * @throws Error if request fails
 */
export async function createTournament(
  tournamentData: CreateTournamentRequest
): Promise<Tournament> {
  const response = await fetch(API_BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(tournamentData)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to create tournament');
  }

  return await response.json();
}

/**
 * Update an existing tournament
 * @param id - Tournament ID
 * @param tournamentData - Updated tournament data
 * @returns Updated tournament object
 * @throws Error if request fails
 */
export async function updateTournament(
  id: string,
  tournamentData: UpdateTournamentRequest
): Promise<Tournament> {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(tournamentData)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to update tournament');
  }

  return await response.json();
}

/**
 * Delete a tournament
 * @param id - Tournament ID
 * @throws Error if request fails
 */
export async function deleteTournamentAPI(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to delete tournament');
  }
}

/**
 * Generate share link for a tournament
 * @param id - Tournament ID
 * @returns Share link object with token
 * @throws Error if request fails
 */
export async function generateShareLink(id: string): Promise<ShareLinkResponse> {
  const response = await fetch(`${API_BASE_URL}/${id}/share`, {
    method: 'POST',
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to generate share link');
  }

  return await response.json();
}

/**
 * Fetch tournament standings
 * @param id - Tournament ID
 * @param round - Optional round number
 * @returns Standings response with standings array and tournament data
 * @throws Error if request fails
 */
export async function fetchStandings(
  id: string,
  round: number | null = null
): Promise<StandingsResponse> {
  const url = round
    ? `${API_BASE_URL}/${id}/standings?round=${round}`
    : `${API_BASE_URL}/${id}/standings`;

  const response = await fetch(url, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch standings');
  }

  return await response.json();
}

/**
 * Fetch tournament ranking (initial ordering)
 * @param id - Tournament ID
 * @returns Ranking array
 * @throws Error if request fails
 */
export async function fetchRanking(id: string): Promise<any[]> {
  const response = await fetch(`${API_BASE_URL}/${id}/ranking`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch ranking');
  }

  const data = await response.json();
  // Backend returns { ranking: [...], tournament: {...} }, extract ranking array
  return data.ranking || data;
}

/**
 * Generate pairings for the next round
 * @param id - Tournament ID
 * @param options - Optional pairing options (roundDate, startTime)
 * @returns Pairing results
 * @throws Error if request fails
 */
export async function generatePairingsAPI(
  id: string,
  options: GeneratePairingsRequest = {}
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/${id}/pairings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(options)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to generate pairings');
  }

  return await response.json();
}

/**
 * Delete the latest round
 * @param id - Tournament ID
 * @returns Result message
 * @throws Error if request fails
 */
export async function deleteLatestRound(id: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/${id}/rounds/latest`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to delete round');
  }

  return await response.json();
}

/**
 * Fetch rounds schedule
 * @param id - Tournament ID
 * @returns Rounds array
 * @throws Error if request fails
 */
export async function fetchRounds(id: string): Promise<Round[]> {
  const response = await fetch(`${API_BASE_URL}/${id}/rounds`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch rounds');
  }

  return await response.json();
}

/**
 * Update round schedule
 * @param id - Tournament ID
 * @param roundNumber - Round number
 * @param roundData - Round data (roundDate, startTime)
 * @returns Updated round
 * @throws Error if request fails
 */
export async function updateRoundSchedule(
  id: string,
  roundNumber: number,
  roundData: UpdateRoundScheduleRequest
): Promise<Round> {
  const response = await fetch(`${API_BASE_URL}/${id}/rounds/${roundNumber}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(roundData)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to update round');
  }

  return await response.json();
}

/**
 * Response from getUnpairedPlayers API
 */
export interface UnpairedPlayersResponse {
  round: number;
  unpairedPlayers: import('../../types/api').Player[];
  byeMatch: {
    id: string;
    player: import('../../types/api').Player;
    result?: string;
  } | null;
  totalPlayers: number;
  pairedCount: number;
}

/**
 * Request to create a manual pairing
 */
export interface CreateManualPairingRequest {
  round: number;
  player1Id: string;
  player2Id?: string;
  isBye?: boolean;
  byePoints?: number;
  replaceByeMatchId?: string;
}

/**
 * Response from createManualPairing API
 */
export interface ManualPairingResponse {
  match: import('../../types/api').Match;
  replacedBye?: boolean;
  message: string;
}

/**
 * Get unpaired players for a specific round
 * @param id - Tournament ID
 * @param roundNumber - Round number
 * @returns Unpaired players and bye information
 * @throws Error if request fails
 */
export async function getUnpairedPlayers(
  id: string,
  roundNumber: number
): Promise<UnpairedPlayersResponse> {
  const response = await fetch(`${API_BASE_URL}/${id}/rounds/${roundNumber}/unpaired`, {
    credentials: 'include'
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to fetch unpaired players');
  }

  return await response.json();
}

/**
 * Create a manual pairing
 * @param id - Tournament ID
 * @param pairingData - Manual pairing data
 * @returns Created match
 * @throws Error if request fails
 */
export async function createManualPairing(
  id: string,
  pairingData: CreateManualPairingRequest
): Promise<ManualPairingResponse> {
  const response = await fetch(`${API_BASE_URL}/${id}/manual-pairing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(pairingData)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to create manual pairing');
  }

  return await response.json();
}

/**
 * Delete a match from a tournament
 * @param tournamentId - Tournament ID
 * @param matchId - Match ID to delete
 * @returns Success response
 * @throws Error if request fails
 */
export async function deleteMatch(
  tournamentId: string,
  matchId: string
): Promise<{ message: string; matchId: string }> {
  const response = await fetch(`${API_BASE_URL}/${tournamentId}/matches/${matchId}`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to delete match');
  }

  return await response.json();
}
